<template>
  <div class="combo-codigo">
    <input
      ref="input"
      :id="id"
      type="text"
      class="form-control combo-codigo__input"
      role="combobox"
      aria-haspopup="listbox"
      aria-autocomplete="list"
      :aria-expanded="aberto ? 'true' : 'false'"
      :aria-controls="`${id}-menu`"
      :aria-activedescendant="destaque >= 0 ? `${id}-opcao-${destaque}` : false"
      :value="textoExibido"
      :placeholder="placeholder"
      :disabled="disabled"
      autocomplete="off"
      spellcheck="false"
      @focus="onFocus"
      @input="onDigita($event.target.value)"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="onEnter"
      @keydown.esc.prevent="onEsc"
      @blur="onBlur"
    />
    <button
      type="button"
      class="combo-codigo__seta"
      tabindex="-1"
      aria-label="Abrir lista de opções"
      :disabled="disabled"
      @mousedown.prevent="toggleMenu"
    >
      <fa-icon icon="chevron-down" aria-hidden="true" />
    </button>

    <ul
      v-if="aberto && opcoesVisiveis.length"
      ref="menu"
      :id="`${id}-menu`"
      class="combo-codigo__menu dropdown-menu show"
      role="listbox"
      :style="menuStyle"
    >
      <li
        v-for="(opcao, indice) in opcoesVisiveis"
        :id="`${id}-opcao-${indice}`"
        :key="`opcao-${indice}-${opcao.id}`"
        :class="{
          active: indice === destaque,
          'combo-codigo__opcao--legado': opcao.legado,
        }"
        class="combo-codigo__opcao dropdown-item"
        role="option"
        :aria-selected="indice === destaque ? 'true' : 'false'"
        @mousedown.prevent
        @click="seleciona(opcao)"
        @mouseenter="destaque = indice"
      >
        {{ labelOpcao(opcao) }}
        <span v-if="opcao.legado" class="combo-codigo__tag-legado">código atual</span>
      </li>
      <!-- role=presentation: nao e uma opcao selecionavel do listbox -->
      <li v-if="opcoesOcultas" class="combo-codigo__rodape" role="presentation">
        + {{ opcoesOcultas }} — refine a busca
      </li>
    </ul>
    <div
      v-else-if="aberto && dicaVazio"
      class="combo-codigo__menu combo-codigo__vazio dropdown-menu show"
      role="status"
      :style="menuStyle"
    >
      {{ dicaVazio }}
    </div>
  </div>
</template>

<script>
import {
  filtraOpcoes,
  isVazio,
  labelOpcao,
  mesmoValor,
  resolveValorDigitado,
  temOpcoes,
} from "@/helpers/opcoesCombo";

let sequencia = 0;

/**
 * Teto de opções renderizadas de uma vez. As listas de código do ERP não têm
 * limite superior conhecido (praça, rede e fornecedor saem de DISTINCT sobre
 * clientes/produtos ativos) e montar milhares de `<li>` a cada tecla travava o
 * combo. Quando há corte, o rodapé do menu informa o total.
 */
const MAX_OPCOES_RENDERIZADAS = 50;

/**
 * Combo de código com digitação livre: o usuário pode escolher uma opção da
 * lista ou digitar o ID diretamente (compatível com códigos legados e registros
 * sincronizados do ERP que não existem mais nas listas da API). Ao digitar, as
 * opções são filtradas por código e descrição.
 *
 * ## Quando usar este componente e quando usar EntidadeAutocomplete
 *
 * O painel tem dois combos com busca, e a diferença é o requisito de digitação
 * livre:
 *
 * - **ComboCodigo (este)** — campos de *código do ERP* em que o valor gravado
 *   pode não existir mais na base (descontos legados, registros sincronizados).
 *   Preserva o valor desconhecido, aceita código digitado e escapa do recorte
 *   de modal (menu `position: fixed`). Seleção única. Usado em Descontos e
 *   disponível para Preços Promocionais e Restrição de Venda.
 * - **`Filtros/EntidadeAutocomplete`** — seleção de *entidade existente* por
 *   busca no servidor, quando o valor sempre existe na base. Suporta seleção
 *   múltipla com tags. Usado em Filtros e AcaoObjeto.
 *
 * Não crie um terceiro: se precisar de múltipla escolha com código livre,
 * estenda um dos dois.
 */
export default {
  name: "ComboCodigo",
  props: {
    value: { default: null },
    opcoes: { type: Array, default: () => [] },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    // Modo assíncrono (ex.: clientes): o filtro é feito pelo servidor via
    // evento `search`; o combo não filtra localmente.
    buscaAsync: { type: Boolean, default: false },
    carregando: { type: Boolean, default: false },
    // Id do input, para o `<label for>` e para os atributos ARIA. Quando não
    // informado é gerado automaticamente.
    inputId: { type: String, default: null },
    // Campo cujo id é textual no ERP (praça, rede, fornecedor): qualquer texto
    // digitado vale como código, não só o que casa com `pareceCodigo`.
    codigoLivre: { type: Boolean, default: false },
  },
  data() {
    sequencia += 1;

    return {
      id: this.inputId || `combo-codigo-${sequencia}`,
      aberto: false,
      focado: false,
      digitou: false,
      texto: "",
      destaque: -1,
      menuStyle: {},
    };
  },
  watch: {
    aberto(valor) {
      if (valor) {
        this.posicionaMenu();
        // Acompanha o scroll de qualquer container (capture) e o resize,
        // já que o menu é fixed para escapar do recorte do modal.
        window.addEventListener("scroll", this.posicionaMenu, true);
        window.addEventListener("resize", this.posicionaMenu);
      } else {
        this.removeListeners();
      }
    },
  },
  beforeDestroy() {
    this.removeListeners();
  },
  computed: {
    textoExibido() {
      if (this.focado) return this.texto;

      const atual = (this.opcoes || []).find(
        (opcao) => opcao && mesmoValor(opcao.id, this.value)
      );

      if (atual) return labelOpcao(atual);

      return isVazio(this.value) ? "" : String(this.value);
    },
    /**
     * Resultado completo do filtro, sem o teto de renderização. É o que decide
     * "sobrou uma única opção" no blur — o corte visual não pode mudar isso.
     */
    opcoesFiltradas() {
      const lista = this.opcoes || [];

      if (this.buscaAsync) {
        // durante a digitação mostra só o que o servidor devolveu: a opção do
        // valor atual no topo competiria com os resultados da busca.
        return this.digitou ? lista.filter((opcao) => !opcao.legado) : lista;
      }

      if (!this.digitou) return lista;

      return filtraOpcoes(lista, this.texto);
    },
    opcoesVisiveis() {
      return this.opcoesFiltradas.slice(0, MAX_OPCOES_RENDERIZADAS);
    },
    /** Quantas opções o filtro achou além das renderizadas. */
    opcoesOcultas() {
      return Math.max(0, this.opcoesFiltradas.length - this.opcoesVisiveis.length);
    },
    dicaVazio() {
      if (this.carregando) return "Buscando...";
      if (this.buscaAsync) {
        return this.digitou && this.texto
          ? "Nenhum resultado — o código digitado será mantido"
          : "Digite para buscar";
      }
      return this.digitou && this.texto
        ? "Nenhuma opção encontrada — digite o código para mantê-lo"
        : "";
    },
  },
  methods: {
    labelOpcao,
    removeListeners() {
      window.removeEventListener("scroll", this.posicionaMenu, true);
      window.removeEventListener("resize", this.posicionaMenu);
    },
    /**
     * Posiciona o menu com position: fixed relativo ao input, para o dropdown
     * escapar do recorte do modal (tab-content com overflow) e poder
     * ultrapassar a borda. Abre para cima quando não há espaço abaixo.
     */
    posicionaMenu() {
      const input = this.$refs.input;
      if (!input) return;

      const rect = input.getBoundingClientRect();
      const alturaMaxima = Math.min(380, window.innerHeight * 0.6);
      const espacoAbaixo = window.innerHeight - rect.bottom - 10;
      const abreParaCima = espacoAbaixo < 160 && rect.top > espacoAbaixo;

      const base = {
        position: "fixed",
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      };

      this.menuStyle = abreParaCima
        ? {
            ...base,
            bottom: `${window.innerHeight - rect.top + 2}px`,
            maxHeight: `${Math.min(alturaMaxima, rect.top - 10)}px`,
          }
        : {
            ...base,
            top: `${rect.bottom + 2}px`,
            maxHeight: `${Math.min(alturaMaxima, espacoAbaixo)}px`,
          };
    },
    onFocus() {
      this.focado = true;
      this.digitou = false;
      this.destaque = -1;
      this.texto = isVazio(this.value) ? "" : String(this.value);
      this.aberto = temOpcoes(this.opcoes) || this.buscaAsync;
    },
    onDigita(valor) {
      this.texto = valor;
      this.digitou = true;
      this.aberto = true;
      this.destaque = -1;
      if (this.buscaAsync) this.$emit("search", valor);
    },
    /**
     * Ao sair do campo, resolve o que foi digitado antes de aceitar (ver
     * `resolveValorDigitado`): como o mesmo input serve para buscar por
     * descrição, gravar o texto cru faria "Filial Centro" virar o valor de
     * filial_id. A digitação livre continua valendo para código.
     */
    onBlur() {
      const estavaFocado = this.focado;
      this.focado = false;
      this.aberto = false;
      if (!estavaFocado) return;

      const { acao, valor } = resolveValorDigitado(
        this.texto,
        this.opcoesFiltradas,
        this.opcoes,
        this.codigoLivre
      );

      if (acao === "descartar") {
        this.texto = isVazio(this.value) ? "" : String(this.value);
        return;
      }

      this.$emit("input", valor);
    },
    onEnter() {
      const opcao = this.aberto ? this.opcoesVisiveis[this.destaque] : null;
      if (opcao) {
        this.seleciona(opcao);
        return;
      }
      this.$refs.input.blur();
    },
    onEsc() {
      // Descarta o que foi digitado e mantém o valor atual.
      this.focado = false;
      this.aberto = false;
      this.$refs.input.blur();
    },
    seleciona(opcao) {
      this.focado = false;
      this.aberto = false;
      this.digitou = false;
      this.$emit("input", opcao.id);
      this.$refs.input.blur();
    },
    toggleMenu() {
      if (this.disabled) return;
      if (this.aberto) {
        this.onEsc();
        return;
      }
      this.$refs.input.focus();
    },
    move(delta) {
      if (!this.aberto) {
        this.aberto = temOpcoes(this.opcoesVisiveis) || this.buscaAsync;
        return;
      }

      const total = this.opcoesVisiveis.length;
      if (!total) return;

      this.destaque = (this.destaque + delta + total) % total;

      this.$nextTick(() => {
        const menu = this.$refs.menu;
        const item = menu && menu.children[this.destaque];
        if (item && item.scrollIntoView) item.scrollIntoView({ block: "nearest" });
      });
    },
  },
};
</script>

<style scoped>
/* Cores vindas das CSS vars do tema (definidas em assets/scss/style.scss);
   borda, sombra e destaque de item vem das classes .dropdown-menu /
   .dropdown-item / .active do Bootstrap. */
.combo-codigo {
  position: relative;
}

.combo-codigo__input {
  padding-right: 26px;
}

.combo-codigo__seta {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 26px;
  border: none;
  background: transparent;
  color: var(--text-subtle);
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.combo-codigo__seta:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Posicao, largura e altura maxima sao definidas inline (position: fixed
   calculado a partir do input), para o menu escapar do recorte do modal e
   poder ultrapassar a borda sem nunca exceder a tela. O z-index precisa
   superar a modal do vue-js-modal. */
.combo-codigo__menu {
  z-index: 10000;
  min-width: 0;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  overflow-y: auto;
}

.combo-codigo__opcao {
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.combo-codigo__opcao--legado {
  border-bottom: 1px solid var(--border-soft);
}

.combo-codigo__opcao.active .combo-codigo__tag-legado {
  color: inherit;
}

.combo-codigo__tag-legado {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-subtle);
  margin-left: 6px;
}

.combo-codigo__vazio {
  padding: 10px;
  font-size: 12px;
  color: var(--text-subtle);
}

.combo-codigo__rodape {
  padding: 6px 10px;
  font-size: 11px;
  color: var(--text-subtle);
  border-top: 1px solid var(--border-soft);
  cursor: default;
}
</style>
